import { useState, useRef, useEffect, useCallback } from 'react'
import { CertificateFileItem } from 'src/generated-types'
import { useTranslation } from 'react-i18next'

interface UseBatchNumberCalculationsProps {
  certificationFiles: CertificateFileItem[]
  forceUpdate?: number
}

interface UseBatchNumberCalculationsReturn {
  parentWidth: number
  containerRef: React.RefObject<HTMLDivElement>
  getItemMaxWidth: () => number
  moreButtonWidth: number
  getOptimalDisplayCount: () => number
  getIndividualItemWidth: (itemCount: number, hasMore: boolean, showAdd: boolean) => number
}

const config = {
  containerPadding: 40,
  moreButtonWidth: 65,
  dividerWidth: 13,
  minItemWidth: 60,
  defaultParentWidth: 200,
  buttonPadding: 12,
}

export const useBatchNumberCalculations = ({
  certificationFiles,
  forceUpdate = 0,
}: UseBatchNumberCalculationsProps): UseBatchNumberCalculationsReturn => {
  const { i18n } = useTranslation()
  const [parentWidth, setParentWidth] = useState(config.defaultParentWidth)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (containerRef.current?.parentElement) {
      const width = containerRef.current.parentElement.getBoundingClientRect().width
      setParentWidth(width - config.containerPadding)
    }
  }, [certificationFiles, forceUpdate])

  const getItemMaxWidth = useCallback((): number => {
    const hasFiles = certificationFiles.length > 0

    if (!hasFiles) {
      return i18n.language === 'en' ? parentWidth : parentWidth * 0.8
    }

    const dividerSpace = config.dividerWidth
    const moreButtonSpace = config.moreButtonWidth

    return parentWidth - moreButtonSpace - dividerSpace
  }, [parentWidth, certificationFiles.length])

  const getOptimalDisplayCount = useCallback((): number => {
    if (certificationFiles.length <= 1) return 1

    const moreButtonSpace = config.moreButtonWidth + config.dividerWidth
    const availableWidth = parentWidth - moreButtonSpace

    let itemCount = 1
    let totalItemWidth = config.minItemWidth + config.buttonPadding

    for (let i = 2; i <= certificationFiles.length; i++) {
      const nextItemWidth = config.minItemWidth + config.buttonPadding + config.dividerWidth

      if (totalItemWidth + nextItemWidth <= availableWidth) {
        totalItemWidth += nextItemWidth
        itemCount = i
      } else {
        break
      }
    }

    if (itemCount >= certificationFiles.length) {
      return certificationFiles.length
    }

    return Math.max(1, itemCount)
  }, [parentWidth, certificationFiles.length])

  const getIndividualItemWidth = useCallback(
    (itemCount: number, hasMore: boolean, showAdd: boolean): number => {
      let availableWidth = parentWidth

      if (hasMore) {
        availableWidth -= config.moreButtonWidth + config.dividerWidth
      }

      if (showAdd) {
        availableWidth -= config.moreButtonWidth + config.dividerWidth
      }

      const dividerSpace = Math.max(0, itemCount - 1) * config.dividerWidth
      const totalButtonPadding = itemCount * config.buttonPadding
      const textWidth = availableWidth - dividerSpace - totalButtonPadding
      const itemWidth = Math.max(config.minItemWidth, Math.floor(textWidth / itemCount))

      return itemWidth
    },
    [parentWidth],
  )

  return {
    parentWidth,
    containerRef,
    getItemMaxWidth,
    moreButtonWidth: config.moreButtonWidth,
    getOptimalDisplayCount,
    getIndividualItemWidth,
  }
}
